import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import ConfirmationModal from "./ConfirmationModal";
import { clusterApi, TenantUsageStats } from "../services/api";
import { useClient } from "../contexts/ClientContext";
import { useAuth } from "../contexts/AuthContext";
import "./ClientManagementModal.css";
import { useToast } from "./Toast";
import { Domain } from "../types";
import { Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface ClientManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDomainUpdated?: () => void;
}

const ClientManagementModal: React.FC<ClientManagementModalProps> = ({
  isOpen,
  onClose,
  onDomainUpdated,
}) => {
  const { showToast } = useToast();

  const { currentClient, setClient } = useClient();
  const { user } = useAuth();
  const [clients, setClients] = useState<Domain[]>([]);
  const [newClientName, setNewClientName] = useState("");
  const [newClientDomain, setNewClientDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Domain | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null);
  const [editDomainValue, setEditDomainValue] = useState("");
  const [isSavingDomain, setIsSavingDomain] = useState(false);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [usageStats, setUsageStats] = useState<TenantUsageStats | null>(null);
  const canDelete = !!user && (user.is_superadmin || user.role === "admin");
  const domainLimitReached = !!usageStats && usageStats.domains_limit === usageStats.domains_used;

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchUsageStats();
      setNewClientName("");
      setNewClientDomain("");
      setError("");
      setSuccessMessage("");
      setEditingDomainId(null);
      setEditingNameId(null);
    }
  }, [isOpen]);

  const fetchClients = async () => {
    setIsLoading(true);
    setError("");

    try {
      const clientList = await clusterApi.getClients();
      setClients(clientList);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      setError("Failed to load clients. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const stats = await clusterApi.getMyUsage();
      setUsageStats(stats);
    } catch (err) {
      console.error("Faield to fetch usage stats in DMM:", err);
    }
  }

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();

    if (user && !user.email_verified) {
      showToast("error", "Please verify your email address first before using this app!")
      return;
    }

    if (!newClientName.trim()) {
      setError("Please enter a website name.");
      return;
    }

    setIsCreating(true);
    setError("");
    setSuccessMessage("");

    try {
      const payload: { name: string; domain?: string } = {
        name: newClientName.trim(),
      };
      if (newClientDomain.trim()) {
        payload.domain = newClientDomain.trim();
      }
      const newClient = await clusterApi.createClient(payload);

      await fetchClients();
      onDomainUpdated?.();

      setNewClientName("");
      setNewClientDomain("");
      setSuccessMessage(`Domain "${newClient.name}" added successfully!`);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to add domain:", error);
      setError(
        error instanceof Error ? error.message : "Failed to add domain"
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleSwitchClient = (clientId: string) => {
    setClient(clientId);
    onClose();
    window.location.reload();
  };

  const handleDeleteClick = (client: Domain) => {
    if (!canDelete) {
      setError("Only tenant admins can delete domains.");
      return;
    }
    setClientToDelete(client);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    if (!canDelete) {
      setError("Only tenant admins can delete domains.");
      return;
    }

    setIsDeleting(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await clusterApi.deleteClient(clientToDelete.id);

      if (clientToDelete.id === currentClient) {
        setClient("");
      }

      await fetchClients();

      setSuccessMessage(
        response.detail ||
          `Domain "${clientToDelete.name}" deleted successfully!`
      );

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to delete domain:", error);
      setError(
        error instanceof Error ? error.message : "Failed to delete domain"
      );
    } finally {
      setIsDeleting(false);
      setClientToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setClientToDelete(null);
  };

  const handleEditDomain = (client: Domain) => {
    setEditingDomainId(client.id);
    setEditDomainValue(client.domain || "");
  };

  const handleSaveDomain = async (clientId: string) => {
    setIsSavingDomain(true);
    setError("");
    try {
      await clusterApi.updateClient(clientId, {
        domain: editDomainValue.trim() || undefined,
      });
      await fetchClients();
      onDomainUpdated?.();
      setEditingDomainId(null);
      setSuccessMessage("Domain updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to update domain:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update domain"
      );
    } finally {
      setIsSavingDomain(false);
    }
  };

  const handleCancelEditDomain = () => {
    setEditingDomainId(null);
    setEditDomainValue("");
  };

  const handleEditName = (client: Domain) => {
    setEditingNameId(client.id);
    setEditNameValue(client.name || "");
  };

  const handleSaveName = async (clientId: string) => {
    const trimmedName = editNameValue.trim();
    if (!trimmedName) {
      setError("Name cannot be empty.");
      return;
    }
    setIsSavingName(true);
    setError("");
    try {
      await clusterApi.updateClient(clientId, { name: trimmedName });
      await fetchClients();
      onDomainUpdated?.();
      setEditingNameId(null);
      setSuccessMessage("Name updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to update name:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update name"
      );
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancelEditName = () => {
    setEditingNameId(null);
    setEditNameValue("");
  };

  // A tenant must always keep at least one domain, so the last remaining domain cannot be deleted.
  const isLastDomain = clients.length <= 1;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Domain Management"
      maxWidth="700px"
    >
      <div className="client-management-modal">
        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
        {!canDelete && (
          <div className="error-message">
            Only tenant admins can delete domains.
          </div>
        )}
        {/* Add New Domain Section */}
        <div className="add-client-section">
          <h3>Add New Domain</h3>
          <form onSubmit={handleCreateClient} className="add-client-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="clientName">Name</label>
                <input
                  type="text"
                  id="clientName"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="Enter website name"
                  disabled={isCreating}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="clientDomain">
                  Domain
                </label>
                <input
                  type="text"
                  id="clientDomain"
                  value={newClientDomain}
                  onChange={(e) => setNewClientDomain(e.target.value)}
                  placeholder="example.com"
                  disabled={isCreating}
                />
              </div>
            </div>
            <Tooltip
              title={domainLimitReached
                ? `You’ve reached the domain limit for your plan (${usageStats?.domains_used}/${usageStats.domains_limit}). Upgrade to add more!`
                : ""
              }
              placement="bottom"
              PopperProps={{
                style: {
                  zIndex: 10000,
                }
              }}
              componentsProps={{
                tooltip: {
                  sx: { maxWidth: 330, textAlign: "center" }
                }
              }}
            >
              <div>
                <button
                  type="submit"
                  className="add-client-btn w-full"
                  disabled={
                    isCreating 
                    || !newClientName.trim() 
                    || !newClientDomain.trim() 
                    || domainLimitReached
                  }
                >
                  {isCreating ? "Creating..." : "Add Domain"}
                </button>
              </div>
            </Tooltip>
            {domainLimitReached && (
              <p className="text-sm text-red-600 mt-1.5 text-center font-medium">
                Domain limit reached ({usageStats?.domains_used}/{usageStats?.domains_limit}).{" "}
                <a href="/admin?tab=billing" className="upgrade-plan-link">Upgrade your plan to add more domain→</a>
              </p>
            )}
          </form>
        </div>

        {/* Clients List Section */}
        <div className="clients-list-section">
          <h3>Existing Domains</h3>

          {isLoading ? (
            <div className="loading-message">Loading domains...</div>
          ) : clients.length === 0 ? (
            <div className="no-clients-message">No domains found.</div>
          ) : (
            <div className="clients-table">
              <div className="clients-header">
                <div className="client-name-col">Name</div>
                <div
                  className="client-domain-col" 
                  style={{
                    paddingLeft: "8px"
                  }}
                >
                  Domain
                </div>
                <div className="client-actions-col">Actions</div>
              </div>

              <div className="clients-body">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className={`client-row ${
                      client.id === currentClient ? "current-client" : ""
                    }`}
                  >
                    <div className="client-name-col">
                      {editingNameId === client.id ? (
                        <div className="domain-edit-row">
                          <input
                            type="text"
                            className="domain-edit-input"
                            value={editNameValue}
                            onChange={(e) => setEditNameValue(e.target.value)}
                            placeholder="Enter website name"
                            disabled={isSavingName}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveName(client.id);
                              if (e.key === "Escape") handleCancelEditName();
                            }}
                          />
                          <Tooltip
                            title="Save"
                            placement="top"
                            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                          >
                            <button
                              className="domain-save-btn"
                              onClick={() => {
                                if (user && !user.email_verified) {
                                  showToast("error", "Please verify your email address first before using this app!")
                                  return;
                                }
                                handleSaveName(client.id)
                              }}
                              disabled={isSavingName}
                              aria-label="Save"
                            >
                              <CheckIcon fontSize="small" />
                            </button>
                          </Tooltip>
                          <Tooltip
                            title="Cancel"
                            placement="top"
                            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                          >
                            <button
                              className="domain-cancel-btn"
                              onClick={handleCancelEditName}
                              disabled={isSavingName}
                              aria-label="Cancel"
                            >
                              <CloseIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      ) : (
                        <span
                          className="client-name"
                          onClick={() => handleEditName(client)}
                          title="Click to edit name"
                          style={{ cursor: "pointer" }}
                        >
                          {client.name}
                          {client.id === currentClient && (
                            <span className="current-badge">Current</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="client-domain-col">
                      {editingDomainId === client.id ? (
                        <div className="domain-edit-row">
                          <input
                            type="text"
                            className="domain-edit-input"
                            value={editDomainValue}
                            onChange={(e) =>
                              setEditDomainValue(e.target.value)
                            }
                            placeholder="example.com"
                            disabled={isSavingDomain}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleSaveDomain(client.id);
                              if (e.key === "Escape") handleCancelEditDomain();
                            }}
                          />
                          <Tooltip
                            title="Save"
                            placement="top"
                            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                          >
                            <button
                              className="domain-save-btn"
                              onClick={() => {
                                if (user && !user.email_verified) {
                                  showToast("error", "Please verify your email address first before using this app!")
                                  return;
                                }
                                handleSaveDomain(client.id)
                              }}
                              disabled={isSavingDomain}
                              aria-label="Save"
                            >
                              <CheckIcon fontSize="small" />
                            </button>
                          </Tooltip>
                          <Tooltip
                            title="Cancel"
                            placement="top"
                            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                          >
                            <button
                              className="domain-cancel-btn"
                              onClick={handleCancelEditDomain}
                              disabled={isSavingDomain}
                              aria-label="Cancel"
                            >
                              <CloseIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      ) : (
                        <span
                          className="client-domain"
                          onClick={() => handleEditDomain(client)}
                          title="Click to edit domain"
                        >
                          {client.domain || (
                            <span className="no-domain">Set domain...</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="client-actions-col">
                      <div className="client-actions">
                        {client.id !== currentClient && (
                          <button
                            type="button"
                            className="switch-client-btn"
                            onClick={() => handleSwitchClient(client.id)}
                          >
                            Switch
                          </button>
                        )}
                        {client.id !== currentClient &&
                          !isLastDomain && (
                            <button
                              type="button"
                              className="delete-client-btn"
                              onClick={() => handleDeleteClick(client)}
                              disabled={!canDelete || isDeleting}
                            >
                              {isDeleting && clientToDelete?.id === client.id
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirmationOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Domain"
        message="Deleting a domain will delete all the anchor keywords associated with the domain as well. Do you want to continue?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </Modal>
  );
};

export default ClientManagementModal;
